"use client";

import { Button } from "@cmp-src/primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@cmp-src/primitives/Select";
import { ToggleGroup } from "@cmp-src/primitives/toggle-group";
import { cn } from "@gettactic/helpers";
import {
  setEndTimeWithDayOverflow,
  useAuthenticated,
  useDefaultOfficeHours,
  useFloors,
  useMyAgenda,
  useUserListByIds
} from "@gettactic/hooks";
import { addMinutes, format } from "date-fns";
import {
  ArrowUpRight,
  CalendarPlus,
  Radio,
  Search,
  Wrench
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { DashboardGreeting } from "@/features/dashboard/components/DashboardGreeting";
import { useGlobalSearch } from "@/features/global-search";
import { MapLoadingSpinner } from "@/features/map/components/MapView/MapLoadingSpinner";
import { MapViewEmbedded } from "@/features/map/components/MapView/MapViewEmbedded";
import { useMapModalContext } from "@/features/map/components/modal";
import { goToMap } from "@/features/map/utils/utils-map";
import { NewRequestWizard } from "@/features/workplace-requests/views/queue/NewRequestWizard";
import { useRouter } from "@/lib/useRouter";
import { convertTZ } from "@/lib/utils";

/**
 * Dashboard tab showing a live preview of the current office's map — the
 * viewer's default floor (or their dedicated desk's, when they have one),
 * with quick actions to search, reserve, or file a request, and a link
 * through to the full map.
 */
export const LiveMap: React.FC = () => {
  const {
    userContext: { authenticatedUser, currentOffice, deviceTz }
  } = useAuthenticated();
  const router = useRouter();
  const { open: openGlobalSearch } = useGlobalSearch();
  const { openModal: openMapReservationModal, closeModal: closeMapModal } =
    useMapModalContext();
  const [isRequestWizardOpen, setIsRequestWizardOpen] = useState(false);

  // Shared by the preview and Reserve so they can't disagree — without an
  // explicit date, MapProvider can adopt a stale one left over from a
  // previous /map visit instead of today, which the "Live" label promises.
  const today = format(new Date(), "yyyy-MM-dd");

  // A stable "now" for the embedded preview's availability window — computed
  // once so it doesn't drift (and remount the map) on every render. The
  // schedulable-resources lookup needs a real, non-zero start/end window;
  // a bare date defaults to midnight and a zero-length window errors.
  const [previewStartTime] = useState(() => new Date());

  // A desk dedicated to the viewer, if any — regardless of which office is
  // currently selected elsewhere in the app. dedicated_resources is only
  // present on the single-user detail endpoint (GET /v1/users/{id}), not on
  // the session's own "me" payload, so it has to be fetched separately.
  const viewerId = authenticatedUser.user?.id;
  const { data: viewerDetails } = useUserListByIds(viewerId ? [viewerId] : []);
  const dedicatedResources = viewerDetails?.[0]?.dedicated_resources;
  // Prefer an actual desk over another dedicated resource type (e.g. a
  // parking space), same reasoning as the reservation preference below.
  const primaryDedicatedResource =
    dedicatedResources?.find(
      (resource) => resource.type === "desk" || resource.type === "workspace"
    ) ?? dedicatedResources?.[0];

  const { data: myAgenda } = useMyAgenda({ from: today, user_tz: deviceTz });
  const todaysSchedules = myAgenda?.agenda?.[today]?.schedules;
  // Among today's reservations, prefer a desk over anything else (a meeting
  // room booking shouldn't outrank the desk the viewer is actually sitting
  // at just because it happens to come first in the list).
  const todaysSchedule =
    todaysSchedules?.find(
      (schedule) =>
        schedule.resource.type === "desk" ||
        schedule.resource.type === "workspace"
    ) ?? todaysSchedules?.[0];

  // Which office to show: a dedicated desk's office, then today's
  // reservation's office, then the viewer's own default office, then
  // whatever office is currently selected elsewhere in the app.
  const officeId =
    primaryDedicatedResource?.office_id ??
    todaysSchedule?.resource.office_id ??
    authenticatedUser.user?.organization_user.default_office_id ??
    currentOffice?.id ??
    null;

  // MapProvider reads these wall-clock strings back as time in the resolved
  // office's own timezone (matching its own getInitialTimeZone() fallback
  // below), not the viewer's — formatting from the raw browser-local Date
  // would shift "now" whenever the viewer isn't in that timezone.
  const previewOffice =
    (officeId && authenticatedUser?.offices?.byId?.[officeId]) || currentOffice;
  const previewOfficeTimeZone = previewOffice.time_zone;

  // Preview availability window: either "right now" (Live) or the office's
  // configured working hours for today (Office hours, the default — most
  // people are looking to book for the day, not this exact minute).
  const [previewMode, setPreviewMode] = useState<"officeHours" | "live">(
    "officeHours"
  );
  const defaultOfficeHoursCalculator = useDefaultOfficeHours(previewOffice);
  const officeHoursWindow = useMemo(() => {
    const officeDay = convertTZ(previewStartTime, previewOfficeTimeZone);
    const hours = defaultOfficeHoursCalculator(officeDay);
    if (!hours) return null;
    const start = new Date(officeDay.getTime());
    start.setHours(hours.startHour, hours.startMinutes, 0);
    const end = new Date(officeDay.getTime());
    setEndTimeWithDayOverflow(end, start, hours.endHour, hours.endMinutes);
    return { start, end };
  }, [previewStartTime, previewOfficeTimeZone, defaultOfficeHoursCalculator]);

  // "9:00 AM – 5:00 PM" reads at a glance; "Office hours" doesn't say when
  // those hours actually are.
  const officeHoursLabel = officeHoursWindow
    ? `${format(officeHoursWindow.start, "h:mm a")} – ${format(officeHoursWindow.end, "h:mm a")}`
    : "Office hours";

  // "Live" = available for the next hour, starting right now — deliberately
  // independent of the Office hours window (not clamped to closing time) so
  // the two modes always query genuinely different ranges instead of
  // silently coinciding for most of the day.
  const liveWindow = useMemo(() => {
    const start = convertTZ(previewStartTime, previewOfficeTimeZone);
    return { start, end: addMinutes(start, 60) };
  }, [previewStartTime, previewOfficeTimeZone]);

  const previewStartTimeStr =
    previewMode === "officeHours" && officeHoursWindow
      ? format(officeHoursWindow.start, "yyyy-MM-dd'T'HH:mm:ss")
      : format(liveWindow.start, "yyyy-MM-dd'T'HH:mm:ss");
  const previewEndTimeStr =
    previewMode === "officeHours" && officeHoursWindow
      ? format(officeHoursWindow.end, "yyyy-MM-dd'T'HH:mm:ss")
      : format(liveWindow.end, "yyyy-MM-dd'T'HH:mm:ss");

  // Until officeHoursWindow resolves, the strings above silently fall back to
  // the live window even in Office hours mode — querying the map with that
  // mismatched range (labeled "Office hours" but actually "Live") would show
  // wrong data and then remount once the real hours load. Wait instead.
  const isPreviewWindowReady = previewMode === "live" || !!officeHoursWindow;

  const { floors } = useFloors(officeId, null);

  // The dedicated desk that's actually on the resolved office (a viewer can
  // have desks dedicated across more than one office), preferring an actual
  // desk over another dedicated resource type in that same office.
  const dedicatedDesk = useMemo(() => {
    const onThisOffice = dedicatedResources?.filter(
      (resource) => resource.office_id === officeId
    );
    return (
      onThisOffice?.find(
        (resource) => resource.type === "desk" || resource.type === "workspace"
      ) ?? onThisOffice?.[0]
    );
  }, [dedicatedResources, officeId]);

  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);

  // Resolve the floor to show once floors have loaded: the dedicated desk's
  // floor, then today's reservation's floor (if it's on this office), then
  // the viewer's own default floor, then the first by weight. A ref-free
  // derivation (not an effect) so switching office recomputes immediately
  // instead of flashing the previous office's floor first.
  const resolvedFloorId = useMemo(() => {
    if (selectedFloorId && floors?.some((f) => f.id === selectedFloorId)) {
      return selectedFloorId;
    }
    if (dedicatedDesk?.parent_id) return dedicatedDesk.parent_id;
    if (
      todaysSchedule?.resource.office_id === officeId &&
      floors?.some((f) => f.id === todaysSchedule.resource.floor.id)
    ) {
      return todaysSchedule.resource.floor.id;
    }
    const defaultAreaId =
      authenticatedUser.user?.organization_user.default_area_id;
    if (defaultAreaId && floors?.some((f) => f.id === defaultAreaId)) {
      return defaultAreaId;
    }
    return floors?.[0]?.id ?? null;
  }, [
    selectedFloorId,
    floors,
    dedicatedDesk?.parent_id,
    todaysSchedule,
    officeId,
    authenticatedUser.user?.organization_user.default_area_id
  ]);

  const isDeskOnThisFloor =
    !!dedicatedDesk && dedicatedDesk.parent_id === resolvedFloorId;

  const isReservationOnThisFloor =
    !!todaysSchedule &&
    todaysSchedule.resource.office_id === officeId &&
    todaysSchedule.resource.floor.id === resolvedFloorId;

  // Pulses the viewer's own spot on the map — their dedicated desk, or
  // today's reservation when they don't have one on this floor — so it's
  // unmistakable at a glance.
  const focusResourceId = isDeskOnThisFloor
    ? dedicatedDesk?.id
    : isReservationOnThisFloor
      ? todaysSchedule?.resource.id
      : undefined;

  if (!officeId) return null;

  return (
    <div className="mt-4 flex flex-col gap-6 md:mt-16">
      <DashboardGreeting name={authenticatedUser.user?.name ?? ""} />

      <div className="flex items-center justify-between gap-3">
        {floors && floors.length > 0 && resolvedFloorId ? (
          <Select
            value={resolvedFloorId}
            onValueChange={(id) => setSelectedFloorId(id)}
          >
            <SelectTrigger className="h-8 min-h-0 w-auto min-w-0 px-2 text-xs">
              <SelectValue>
                {floors.find((f) => f.id === resolvedFloorId)?.name}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {floors.map((floor) => (
                <SelectItem key={floor.id} value={floor.id}>
                  {floor.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div />
        )}

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconLeft={<Search className="h-4 w-4" />}
            onClick={() => openGlobalSearch()}
          >
            Search
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconLeft={<CalendarPlus className="h-4 w-4" />}
            onClick={() =>
              openMapReservationModal({
                date: today,
                officeId,
                floorId: resolvedFloorId ?? undefined,
                reservationModalIsOpen: true,
                onSuccess: () => closeMapModal()
              })
            }
          >
            Reserve
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconLeft={<Wrench className="h-4 w-4" />}
            onClick={() => setIsRequestWizardOpen(true)}
          >
            Request
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50">
        <div className="flex items-center justify-between px-4 pt-3">
          <ToggleGroup
            items={[
              { value: "officeHours", label: officeHoursLabel },
              {
                value: "live",
                label: (
                  <span className="flex items-center gap-1.5">
                    <Radio className="h-3.5 w-3.5" />
                    Live
                  </span>
                )
              }
            ]}
            value={previewMode}
            onValueChange={(value) =>
              setPreviewMode(value as "officeHours" | "live")
            }
            className="font-inter text-xs [&_button]:px-3 [&_button]:py-1"
          />
          <div className="flex items-center gap-3">
            {isDeskOnThisFloor && dedicatedDesk ? (
              <button
                type="button"
                className="flex items-center gap-1.5 font-inter text-neutral-700 text-xs hover:text-neutral-900"
                onClick={() =>
                  goToMap(
                    router,
                    dedicatedDesk.id,
                    officeId,
                    today,
                    resolvedFloorId
                  )
                }
              >
                Your desk
              </button>
            ) : null}
            <button
              type="button"
              className="flex items-center gap-1 font-inter text-neutral-500 text-xs hover:text-neutral-700"
              onClick={() =>
                goToMap(router, null, officeId, today, resolvedFloorId)
              }
            >
              Go to map
              <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>
        </div>
        <div className={cn("relative h-[420px] w-full")}>
          {resolvedFloorId && isPreviewWindowReady ? (
            <MapViewEmbedded
              officeId={officeId}
              floorId={resolvedFloorId}
              date={previewStartTimeStr}
              endDate={previewEndTimeStr}
              focussedResourceId={focusResourceId}
              disableInitialFocusZoom
              enableControls
              showMiniMap={false}
              onReserveResource={(resource) =>
                openMapReservationModal({
                  date: today,
                  officeId,
                  floorId: resolvedFloorId ?? undefined,
                  resourceId: resource.id,
                  reservationModalIsOpen: true,
                  onSuccess: () => closeMapModal()
                })
              }
            />
          ) : resolvedFloorId ? (
            <div className="flex h-full w-full items-center justify-center">
              <MapLoadingSpinner />
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center text-neutral-400 text-sm">
              This office has no floors to show yet.
            </div>
          )}
        </div>
      </div>

      <NewRequestWizard
        isOpen={isRequestWizardOpen}
        onOpenChange={setIsRequestWizardOpen}
      />
    </div>
  );
};
